import { type Editor } from '@tiptap/react';
import { getInlineEditPluginState } from './inline-edit-decorations';

import { isEditorLive } from '@heidi/editor/core';
const BAR_OFFSET_PX = 8;
/** Minimum clearance between the bar and the viewport edges. */
const VIEWPORT_PADDING_PX = 8;

export interface BarPosition {
  top: number;
  left: number;
  /** Upper bound on the bar's width; it shrinks to its content below this. */
  maxWidth: number;
}

const getRangeRect = (editor: Editor): DOMRect | null => {
  const range = getInlineEditPluginState(editor)?.range;
  if (!range || !isEditorLive(editor, 'inline-edit-bar-position.range')) return null;
  try {
    const start = editor.view.domAtPos(range.from);
    const end = editor.view.domAtPos(range.to);
    const domRange = editor.view.dom.ownerDocument.createRange();
    domRange.setStart(start.node, start.offset);
    domRange.setEnd(end.node, end.offset);
    const rect = domRange.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return null;
    return rect;
  } catch {
    return null;
  }
};

export const computeBarPosition = (
  editor: Editor,
  barHeight: number
): BarPosition | null => {
  if (!isEditorLive(editor, 'inline-edit-bar-position')) return null;

  const editorEl = editor.view.dom;
  const editorRect = editorEl.getBoundingClientRect();
  const editorStyle = window.getComputedStyle(editorEl);
  const paddingLeft = Number.parseFloat(editorStyle.paddingLeft) || 0;
  const paddingRight = Number.parseFloat(editorStyle.paddingRight) || 0;
  const textLeft = editorRect.left + paddingLeft;
  const textWidth = editorRect.width - paddingLeft - paddingRight;
  const viewportHeight = window.innerHeight;
  const maxTop =
    barHeight > 0
      ? Math.max(VIEWPORT_PADDING_PX, viewportHeight - barHeight - VIEWPORT_PADDING_PX)
      : viewportHeight - VIEWPORT_PADDING_PX;

  // Fall back to the editor column when the selection is off-screen or
  // domAtPos cannot resolve a non-zero rect — controls must stay reachable.
  const rangeRect = getRangeRect(editor);
  const anchorBottom = rangeRect?.bottom ?? editorRect.top;
  const anchorTop = rangeRect?.top ?? editorRect.top;

  let top = anchorBottom + BAR_OFFSET_PX;
  if (barHeight > 0 && top + barHeight > viewportHeight - VIEWPORT_PADDING_PX) {
    const flippedTop = anchorTop - BAR_OFFSET_PX - barHeight;
    top =
      flippedTop >= VIEWPORT_PADDING_PX
        ? flippedTop
        : Math.max(VIEWPORT_PADDING_PX, viewportHeight - barHeight - VIEWPORT_PADDING_PX);
  }

  top = Math.min(Math.max(top, VIEWPORT_PADDING_PX), maxTop);

  return { top, left: textLeft, maxWidth: textWidth };
};
